import { Content, List, Root, Trigger } from "@radix-ui/react-tabs";
import type { ComponentPropsWithoutRef } from "react";
import { Icon, type IconSymbol } from "../components/icon";
import { cn } from "../lib/utils";
import { Badge } from "./badge";

interface TabsTriggerProps extends ComponentPropsWithoutRef<typeof Trigger> {
  icon?: IconSymbol;
  badge?: string | number;
}

const Tabs = Root;

type TabsListProps = ComponentPropsWithoutRef<typeof List> & {
  ref?: React.Ref<HTMLDivElement>;
};
const TabsList = ({ className, ref, ...props }: TabsListProps) => (
  <List
    ref={ref}
    className={cn(
      "flex justify-start border-b border-gray-200 h-6 items-stretch",
      className,
    )}
    {...props}
  />
);
TabsList.displayName = List.displayName;

type TabsTriggerPropsWithRef = TabsTriggerProps & {
  ref?: React.Ref<HTMLButtonElement>;
};
const TabsTrigger = ({
  className,
  icon,
  badge,
  children,
  asChild,
  ref,
  ...props
}: TabsTriggerPropsWithRef) => (
  <Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap first:ml-0 mx-3 text-[14px] font-base text-gray-900 gap-2 mb-[1px]",
      "hover:text-primary",
      "h-full",
      "data-[state=active]:border-b-2 data-[state=active]:border-primary  data-[state=active]:font-title data-[state=active]:text-pink-700 data-[state=active]:-mb-[1px]",
      className,
    )}
    {...props}
  >
    {icon && <Icon icon={icon} className="text-gray-500" size={16} />}
    {children}
    {badge && (
      <Badge
        variant="filled"
        className="px-1 rounded-sm min-w-4 h-4 font-title"
      >
        {badge}
      </Badge>
    )}
  </Trigger>
);
TabsTrigger.displayName = Trigger.displayName;

type TabsContentProps = ComponentPropsWithoutRef<typeof Content> & {
  ref?: React.Ref<HTMLDivElement>;
};
const TabsContent = ({ className, ref, ...props }: TabsContentProps) => (
  <Content ref={ref} className={cn("mt-6", className)} {...props} />
);
TabsContent.displayName = Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
